import { yupResolver } from '@hookform/resolvers/yup'
import { captureException } from '@sentry/react'
import { useQueryClient } from '@tanstack/react-query'
import { Controller, useForm } from 'react-hook-form'
import * as Yup from 'yup'

import type { ReactElement } from 'react'
import type { TApp } from '~/types/cluster'

import Button from '~/components/Buttons/Button'
import CreatableSelectInput from '~/components/Inputs/ReactHookForm/CreatableSelectInput'
import Modal from '~/components/Modal'
import { ModalFooter, ModalHeader, ModalBody } from '~/components/Modal/Layout'
import { FORM_ERRORS } from '~/const'
import usePreventScroll from '~/hooks/usePreventScroll'
import { useBatchAddTagsMutation, useSetTagsMutation, keys as clustersKeys } from '~/queries/clusters'
import { reactSelectMenuPositionDef } from '~/utils/options'
import { displayToast } from '~/utils/toast'
import { RequiredYupOption } from '~/utils/yup'

import './styles.scss'

const MODAL_ID = 'edit-app-tags-modal'

interface FormValues {
  tags: IOption[]
}

const validationSchema = Yup.object().shape({
  tags: Yup.array().of(RequiredYupOption).required(FORM_ERRORS.REQUIRED),
})

interface Props {
  apps: TApp[]
  clusterId: string
  clusterTags: string[]
  isOpen: boolean
  onClose: VoidFunction
  onSuccess: (params: { isMulti: boolean; storeIds: string[]; tags: string[] }) => void
}

const EditAppTagsModal = ({ apps, clusterId, clusterTags, isOpen, onClose, onSuccess }: Props): ReactElement => {
  /** Local state */

  const isActionMulti = apps.length > 1

  const queryClient = useQueryClient()

  const {
    control,
    formState: { isSubmitting },
    handleSubmit,
    reset,
  } = useForm<FormValues>({
    defaultValues: {
      tags: isActionMulti ? [] : apps[0].tags.map((tag) => ({ label: tag, value: tag })),
    },
    resolver: yupResolver(validationSchema),
  })

  const options = clusterTags
    .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()))
    .map((el) => ({ label: el, value: el }))

  /** Queries & Mutations */

  const { mutateAsync: batchAddTags } = useBatchAddTagsMutation()

  const { mutateAsync: setTags } = useSetTagsMutation()

  /** Effects */

  usePreventScroll(isOpen)

  /** Handlers */

  const handleFormSubmit = async (values: FormValues) => {
    const newTags = values.tags.map((t) => t.value)
    const storeIds = isActionMulti ? apps.map(({ storeId }) => storeId) : [apps[0].storeId]

    try {
      if (isActionMulti) {
        await batchAddTags({
          apps: apps.map(({ deviceType, storeId }) => ({ deviceType, storeId })),
          clusterId,
          tags: newTags,
        })
      } else {
        await setTags({ clusterId, deviceType: apps[0].deviceType, storeId: apps[0].storeId, tags: newTags })
      }
      await queryClient.invalidateQueries({ queryKey: clustersKeys.listAppsForClusters({ clusterId }) })
      await queryClient.invalidateQueries({ queryKey: clustersKeys.tags(clusterId) })
      onSuccess({ isMulti: isActionMulti, storeIds, tags: newTags })
      displayToast(`Tags successfully ${isActionMulti ? 'added' : 'edited'}`, 'success')
    } catch (e) {
      captureException(e)
      displayToast(`Tags could not be ${isActionMulti ? 'added' : 'edited'}`, 'error')
    }
  }

  const handleModalClose = () => {
    reset()
    onClose()
  }

  /** Render */

  return (
    <Modal className="edit-app-tags-modal" id={MODAL_ID} isOpen={isOpen} onCloseModal={handleModalClose}>
      <ModalHeader>{isActionMulti ? 'Add' : 'Edit'} app tags</ModalHeader>
      <form className="edit-app-tags-form" onSubmit={handleSubmit(handleFormSubmit)}>
        <ModalBody className="edit-app-tags-form__modal-body">
          <Controller
            control={control}
            name="tags"
            render={({ field }) => (
              <CreatableSelectInput
                {...field}
                {...reactSelectMenuPositionDef(MODAL_ID)}
                defaultValue={isActionMulti ? [] : apps[0].tags?.map((el) => ({ label: el, value: el }))}
                isMulti
                label="Tags"
                options={options}
                placeholder='Type a tag and press "Enter"'
              />
            )}
          />
        </ModalBody>
        <ModalFooter>
          <Button ghost onClick={onClose}>
            Cancel
          </Button>
          <Button disabled={isSubmitting} type="submit">
            {isActionMulti ? 'Add' : 'Update'}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  )
}

export default EditAppTagsModal
